/**
 * mt-cursor-circle
 * 让光标cursor跟随鼠标移动
 * 使用单个全局光标
 */

(function() {
  'use strict';

  let circle = null;
  let currentElement = null;
  let isHovering = false;
  let lastMoveAt = 0;
  let idleTimer = null;
  let lastAutoColor = null;
  let rafPending = false;
  let lastMouseX = 0;
  let lastMouseY = 0;

  // 等待DOM加载完成
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // 创建全局光标元素
    circle = document.createElement('div');
    circle.className = 'mt-cursor-circle-element';
    circle.style.cssText = `
      position: fixed;
      width: 40px;
      height: 40px;
      border: 2px solid currentColor;
      border-style: solid;
      border-radius: 50%;
      pointer-events: none;
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.5);
      transition: opacity 0.2s ease, transform 0.2s ease, border-color 0.25s ease;
      z-index: 9999;
      /* difference 在部分背景上会显得不清晰；默认用 normal 更稳定 */
      mix-blend-mode: normal;
      left: -100px;
      top: -100px;
    `;
    document.body.appendChild(circle);

    // 监听全局鼠标移动
    document.addEventListener('mousemove', handleMouseMove);
  }

  function handleMouseMove(e) {
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;

    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        handleMouseMoveRaf();
      });
    }
  }

  function handleMouseMoveRaf() {
    const x = lastMouseX;
    const y = lastMouseY;

    // 更新光标位置
    if (circle) {
      circle.style.left = x + 'px';
      circle.style.top = y + 'px';
    }

    // 检查鼠标是否在可点击元素上
    const hoveredElement = document.elementFromPoint(x, y);
    const cursorSelector =
      '.mt-cursor-circle, .mt-cursor-circle-hover, .mt-cursor-circle-link, .mt-cursor-circle-button';

    const targetElement =
      hoveredElement && (hoveredElement.matches(cursorSelector) ? hoveredElement : hoveredElement.closest(cursorSelector));

    if (targetElement) {
      
      if (targetElement !== currentElement) {
        // 切换到新元素
        if (currentElement) {
          removeElementListeners(currentElement);
        }
        currentElement = targetElement;
        setupElementListeners(targetElement);
        updateCircleStyle(targetElement, hoveredElement);
        isHovering = true;
        showCircle();
      }
      // 同一个元素内持续移动时，确保光标保持显示（避免错过 mouseenter）
      if (!isHovering) {
        isHovering = true;
        showCircle();
      }

      // 自动对比色：随环境实时变化（根据鼠标下方的有效背景色决定黑/白）
      if (circle && currentElement) {
        updateAutoContrastColor(currentElement, hoveredElement);
      }

      // 鼠标移动中：移除 idle 动效，并重新计时
      markMoving();
    } else {
      // 不在任何可点击元素上
      if (currentElement) {
        removeElementListeners(currentElement);
        currentElement = null;
      }
      isHovering = false;
      hideCircle();
      clearIdle();
    }
  }

  function setupElementListeners(element) {
    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
  }

  function removeElementListeners(element) {
    element.removeEventListener('mouseenter', handleMouseEnter);
    element.removeEventListener('mouseleave', handleMouseLeave);
  }

  function handleMouseEnter() {
    isHovering = true;
    showCircle();
  }

  function handleMouseLeave() {
    isHovering = false;
    hideCircle();
    clearIdle();
  }

  function showCircle() {
    if (circle && isHovering) {
      circle.style.opacity = '1';
      circle.style.transform = 'translate(-50%, -50%) scale(1)';
    }
  }

  function hideCircle() {
    if (circle) {
      circle.style.opacity = '0';
      circle.style.transform = 'translate(-50%, -50%) scale(0.5)';
    }
  }

  function updateCircleStyle(element, hoveredElement) {
    if (!circle || !element) return;

    // 获取元素的计算样式
    const computedStyle = window.getComputedStyle(element);
    const size = (computedStyle.getPropertyValue('--mt-cursor-size') || '40px').trim();
    const borderWidth = (computedStyle.getPropertyValue('--mt-cursor-border-width') || '2px').trim();
    const colorVar = (computedStyle.getPropertyValue('--mt-cursor-color') || 'auto').trim();
    const blendMode = (computedStyle.getPropertyValue('--mt-cursor-blend-mode') || 'normal').trim();
    const diffColor = (computedStyle.getPropertyValue('--mt-cursor-difference-color') || '#ffffff').trim();
    const glow = (computedStyle.getPropertyValue('--mt-cursor-glow') || '').trim();

    // 应用基础样式
    circle.style.width = size;
    circle.style.height = size;
    circle.style.borderWidth = borderWidth;
    circle.style.borderStyle = 'solid';
    circle.style.mixBlendMode = blendMode || 'normal';
    circle.style.boxShadow = glow || 'none';

    // 颜色优先级：
    // 1) 变体类（green/white/black）——后面会覆盖
    // 2) data-mt-cursor-color / data-color
    // 3) --mt-cursor-color（非 auto）
    // 4) auto：根据背景亮度选黑/白
    const sceneColor = findSceneColor(element) || findSceneColor(hoveredElement);
    if (sceneColor) {
      circle.style.borderColor = sceneColor;
      lastAutoColor = null;
    } else if (colorVar && colorVar !== 'auto') {
      circle.style.borderColor = colorVar;
      lastAutoColor = null;
    } else {
      const color =
        (blendMode === 'difference' ? (diffColor || '#ffffff') : pickContrastingColorFromBg(hoveredElement || element));
      circle.style.borderColor = color;
      lastAutoColor = color;
    }

    if (element.classList.contains('mt-cursor-circle--green')) {
      circle.style.borderColor = '#3ac054';
      circle.style.mixBlendMode = blendMode || 'normal';
    } else if (element.classList.contains('mt-cursor-circle--white')) {
      circle.style.borderColor = '#ffffff';
      circle.style.mixBlendMode = blendMode || 'normal';
    } else if (element.classList.contains('mt-cursor-circle--black')) {
      circle.style.borderColor = '#000000';
      circle.style.mixBlendMode = blendMode || 'normal';
    }

    if (element.classList.contains('mt-cursor-circle--small')) {
      circle.style.width = '40px';
      circle.style.height = '40px';
      circle.style.borderWidth = '1px';
    } else if (element.classList.contains('mt-cursor-circle--large')) {
      circle.style.width = '44px';
      circle.style.height = '44px';
      circle.style.borderWidth = '1px';
    }
  }

  function updateAutoContrastColor(element, hoveredElement) {
    if (!circle || !element) return;
    const computedStyle = window.getComputedStyle(element);
    const colorVar = (computedStyle.getPropertyValue('--mt-cursor-color') || 'auto').trim();
    const blendMode = (computedStyle.getPropertyValue('--mt-cursor-blend-mode') || 'normal').trim();
    const diffColor = (computedStyle.getPropertyValue('--mt-cursor-difference-color') || '#ffffff').trim();
    if (colorVar && colorVar !== 'auto') return; 

    if (
      element.classList.contains('mt-cursor-circle--green') ||
      element.classList.contains('mt-cursor-circle--white') ||
      element.classList.contains('mt-cursor-circle--black')
    ) {
      return;
    }

    const sceneColor = findSceneColor(element) || findSceneColor(hoveredElement);
    if (sceneColor) {
      if (sceneColor !== lastAutoColor) {
        circle.style.borderColor = sceneColor;
        lastAutoColor = sceneColor;
      }
      return;
    }

    const next =
      blendMode === 'difference' ? (diffColor || '#ffffff') : pickContrastingColorFromBg(hoveredElement || element);
    if (next && next !== lastAutoColor) {
      circle.style.borderColor = next;
      lastAutoColor = next;
    }
  }

  function findSceneColor(startEl) {
    // 兼容 data-color，同时支持更明确的 data-mt-cursor-color
    if (!startEl) return null;
    const node =
      startEl.nodeType === 1
        ? (startEl.matches('[data-mt-cursor-color], [data-color]') ? startEl : startEl.closest('[data-mt-cursor-color], [data-color]'))
        : null;
    if (!node) return null;
    const val = (node.getAttribute('data-mt-cursor-color') || node.getAttribute('data-color') || '').trim();
    return val || null;
  }

  function pickContrastingColorFromBg(startEl) {
    const rgb = findEffectiveBackgroundRgb(startEl);
    if (!rgb) return '#000000';
    const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
    // 亮背景用黑色，暗背景用白色
    return lum > 0.6 ? '#000000' : '#ffffff';
  }

  function findEffectiveBackgroundRgb(el) {
    // 从当前元素向上找第一个非透明 background-color
    let node = el;
    while (node && node.nodeType === 1) {
      const bg = window.getComputedStyle(node).backgroundColor;
      const parsed = parseCssColorToRgb(bg);
      if (parsed && parsed.a > 0) return parsed;
      node = node.parentElement;
    }
    // fallback：body
    const bodyBg = window.getComputedStyle(document.body).backgroundColor;
    return parseCssColorToRgb(bodyBg);
  }

  function parseCssColorToRgb(color) {
    if (!color) return null;
    // rgb(r, g, b) / rgba(r, g, b, a)
    const m = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i);
    if (!m) return null;
    return {
      r: clamp255(Number(m[1])),
      g: clamp255(Number(m[2])),
      b: clamp255(Number(m[3])),
      a: m[4] === undefined ? 1 : Math.max(0, Math.min(1, Number(m[4]))),
    };
  }

  function clamp255(n) {
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(255, n));
  }

  function relativeLuminance(r, g, b) {
    // sRGB -> linear
    const rs = srgbToLinear(r / 255);
    const gs = srgbToLinear(g / 255);
    const bs = srgbToLinear(b / 255);
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function markMoving() {
    if (!circle) return;
    circle.classList.remove('is-idle');
    lastMoveAt = Date.now();
    if (idleTimer) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      // 只有在仍然 hover 状态下才进入 idle
      if (circle && isHovering) {
        circle.classList.add('is-idle');
      }
    }, 220);
  }

  function clearIdle() {
    if (!circle) return;
    circle.classList.remove('is-idle');
    if (idleTimer) window.clearTimeout(idleTimer);
    idleTimer = null;
  }
})();



